import PageShell from "@/components/PageShell";
import {
  Phone, Siren, Heart, Flame, Shield, AlertTriangle, Stethoscope, Ear, Hand,
  Baby, Users, MessageSquare, HelpCircle, Globe, Briefcase, Home, Hammer,
} from "lucide-react";

const SECTIONS = [
  {
    id: "urgences",
    title: "Urgences vitales",
    color: "from-pink-600 to-red-600",
    icon: Siren,
    items: [
      { number: "112", label: "Numéro d'urgence européen", desc: "Toutes urgences (police, pompiers, SAMU) — depuis n'importe quel téléphone", icon: AlertTriangle, color: "bg-red-500", tip: "Gratuit, 24h/24, sans crédit" },
      { number: "15",  label: "SAMU", desc: "Urgence médicale, malaise, perte de conscience", icon: Heart, color: "bg-red-600" },
      { number: "17",  label: "Police / Gendarmerie", desc: "Sécurité, agression, intrusion", icon: Shield, color: "bg-blue-700" },
      { number: "18",  label: "Pompiers", desc: "Incendie, accident, secours à personne", icon: Flame, color: "bg-orange-600" },
      { number: "114", label: "Urgences pour personnes sourdes ou malentendantes", desc: "Par SMS ou Fax — accessible 24h/24", icon: Ear, color: "bg-plum-700" },
      { number: "196", label: "Urgences en mer (CROSS)", desc: "Sauvetage en mer — étang de Berre, plages d'Istres", icon: AlertTriangle, color: "bg-mint-700" },
    ],
  },
  {
    id: "sante",
    title: "Santé & soins",
    color: "from-mint-600 to-mint-700",
    icon: Stethoscope,
    items: [
      { number: "3237",         label: "Pharmacies de garde", desc: "Trouver une pharmacie ouverte — 24h/24", icon: Stethoscope, color: "bg-mint-600" },
      { number: "0800 130 000", label: "COVID Info Service", desc: "Information sur la COVID-19", icon: Stethoscope, color: "bg-mint-600", tip: "Gratuit" },
      { number: "04 42 56 13 00", label: "Centre hospitalier d'Istres", desc: "Hôpital local — Allée Henri Matisse, 13800 Istres", icon: Heart, color: "bg-mint-700" },
      { number: "0810 333 333", label: "Centre antipoison Marseille", desc: "Intoxications, médicaments, plantes", icon: AlertTriangle, color: "bg-orange-500" },
      { number: "3919",         label: "Violences faites aux femmes", desc: "Écoute, info, orientation — 24h/24 · gratuit · anonyme", icon: Heart, color: "bg-pink-600", tip: "N'apparaît pas sur la facture téléphonique" },
      { number: "119",          label: "Enfance en danger", desc: "Mineurs en danger — 24h/24 · gratuit · anonyme", icon: Baby, color: "bg-pink-700" },
    ],
  },
  {
    id: "social",
    title: "Accompagnement social",
    color: "from-plum-700 to-plum-800",
    icon: Hand,
    items: [
      { number: "115",          label: "SAMU social — Sans-abri", desc: "Hébergement d'urgence, signalement personne en détresse · 24h/24", icon: Home, color: "bg-plum-700" },
      { number: "3977",         label: "Maltraitance personnes âgées et handicapées", desc: "Écoute confidentielle · 9h–19h", icon: Users, color: "bg-plum-600" },
      { number: "3018",         label: "Cyberharcèlement & violences numériques", desc: "Pour les mineurs et leurs parents", icon: MessageSquare, color: "bg-pink-500" },
      { number: "3114",         label: "Prévention du suicide", desc: "Écoute professionnelle · 24h/24 · gratuit · confidentiel", icon: Heart, color: "bg-plum-800" },
      { number: "0 800 235 236", label: "Drogues Info Service", desc: "Cannabis, alcool, jeux d'argent — anonyme · gratuit", icon: HelpCircle, color: "bg-mint-700" },
      { number: "0 800 858 858", label: "SOS Amitié", desc: "Solitude, mal-être — écoute 24h/24", icon: Heart, color: "bg-pink-500" },
    ],
  },
  {
    id: "istres",
    title: "Services Mairie d'Istres",
    color: "from-sun-500 to-sun-600",
    icon: Briefcase,
    items: [
      { number: "04 13 29 50 00", label: "Mairie d'Istres", desc: "Hôtel de Ville — 1 Esplanade Charles de Gaulle", icon: Briefcase, color: "bg-sun-600" },
      { number: "04 42 11 28 80", label: "CCAS Istres", desc: "Centre Communal d'Action Sociale — aides, accompagnement", icon: Hand, color: "bg-sun-700" },
      { number: "04 13 33 21 00", label: "Police municipale", desc: "Sécurité quotidienne, stationnement, tranquillité publique", icon: Shield, color: "bg-blue-700" },
      { number: "04 42 11 22 22", label: "Centre technique municipal", desc: "Voirie, propreté, signalements", icon: Hammer, color: "bg-plum-700" },
      { number: "04 42 81 76 00", label: "Office du Tourisme", desc: "Allée Jean Jaurès — Lun-Sam · 9h-18h", icon: Globe, color: "bg-mint-600" },
      { number: "04 42 56 33 23", label: "Médiathèque d'Istres", desc: "Place de l'Hôtel de Ville", icon: HelpCircle, color: "bg-plum-600" },
    ],
  },
];

export default function NumerosUtilesPage() {
  return (
    <PageShell>
      <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pink-100 text-pink-600 text-[11px] font-bold uppercase tracking-[0.18em] mb-4 border border-pink-200">
            <Phone size={11} strokeWidth={2.5} /> Numéros utiles
          </div>
          <h1 className="heading-display text-[2rem] sm:text-[2.6rem] text-plum-900 leading-tight">
            Tous les <span className="text-pink-600">numéros utiles</span> en cas de besoin
          </h1>
          <p className="text-[14px] text-plum-700/75 mt-3 max-w-2xl mx-auto leading-relaxed">
            Urgences, santé, accompagnement social et services municipaux — gardez cette page à portée de main.
          </p>
        </div>

        {/* Quick anchor nav */}
        <nav className="flex flex-wrap justify-center gap-2 mb-10" aria-label="Sections">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-cream-200 text-plum-700 text-[12px] font-bold hover:bg-cream-50 transition-colors"
              data-testid={`nav-${s.id}`}
            >
              <s.icon size={12} strokeWidth={2.4} />
              {s.title}
            </a>
          ))}
        </nav>

        {/* Sections */}
        <div className="space-y-12">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24" data-testid={`section-${s.id}`}>
              <div className="flex items-center gap-3 mb-5">
                <div className={`shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br ${s.color} text-white grid place-items-center shadow-lg`}>
                  <s.icon size={22} strokeWidth={2.2} />
                </div>
                <h2 className="font-display font-extrabold text-plum-900 text-[1.5rem] sm:text-[1.8rem] leading-none">
                  {s.title}
                </h2>
              </div>

              <ul className="grid sm:grid-cols-2 gap-3">
                {s.items.map((it) => (
                  <li key={it.number}>
                    <a
                      href={`tel:${it.number.replace(/\s/g, "")}`}
                      data-testid={`num-${it.number.replace(/\s/g, "")}`}
                      className="group flex items-stretch gap-3 phenix-card phenix-card-hover bg-white p-4 transition-all hover:-translate-y-0.5"
                    >
                      <div className={`shrink-0 w-14 h-14 rounded-2xl ${it.color} text-white grid place-items-center shadow-md`}>
                        <it.icon size={22} strokeWidth={2.2} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-extrabold text-plum-900 text-[14px] leading-tight mb-0.5 line-clamp-1">
                          {it.label}
                        </div>
                        <div className="text-[11px] text-plum-700/70 leading-snug line-clamp-2 mb-1">
                          {it.desc}
                        </div>
                        {it.tip && (
                          <div className="text-[10px] font-bold text-mint-600 uppercase tracking-wider">
                            {it.tip}
                          </div>
                        )}
                      </div>
                      <div className="shrink-0 self-center">
                        <span className="block font-display font-extrabold text-plum-900 text-[18px] sm:text-[20px] leading-none whitespace-nowrap group-hover:text-pink-600 transition-colors">
                          {it.number}
                        </span>
                        <span className="block text-[10px] text-plum-700/55 mt-0.5 text-right">
                          Appeler
                        </span>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-12 phenix-card bg-cream-100/60 p-5 text-center">
          <div className="inline-flex items-center gap-2 mb-2 text-pink-600 font-display font-extrabold text-[15px]">
            <Siren size={16} /> En cas de doute, composez le 112
          </div>
          <p className="text-[12px] text-plum-700/70 max-w-2xl mx-auto leading-relaxed">
            Le <strong>112</strong> est le numéro d&apos;urgence européen, joignable gratuitement 24h/24, depuis n&apos;importe quel téléphone (même bloqué ou sans crédit) et dans toute l&apos;Europe.
          </p>
        </div>
      </div>
    </PageShell>
  );
}
